import React from 'react';
import { AppProps, NextWebVitalsMetric } from 'next/app';
import Script from 'next/script';
import assetUrls from '@amzn/katal-components/asseturls';
import AppNavbar from 'src/components/AppNavbar';
import RouteHistory from 'src/components/RouteHistory';
import SearchBar from 'src/components/SearchBar';
import RouteProtector from 'src/components/RouteProtector';
import ErrorBoundary from 'src/components/ErrorBoundary';
import { install } from 'resize-observer';
import { Logs, Pages } from 'src/constants/metrics';
import {
  Metrics, getPageFromPathname, logger, getIsSSRPage,
} from 'src/utils';
import { ForumsPageProps } from 'src/types/NextApp';
import StoreProvider from 'src/components/StoreProvider';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import SSRApp from './ssrApp';
import 'src/styles/index.css';
import I18NWrapper from '../components/I18nWrapper';
import UserInfo from '../components/UserInfo';
import GlobalBanners from '../components/GlobalBanners';
import PermissionsModal from '../components/PermissionsModal';
import 'intersection-observer';

// Install ResizeObserver polyfill if it doesn't already exist
if (typeof window !== 'undefined' && !window.ResizeObserver) install();

// Starts the mocking of APIs
// https://github.com/vercel/next.js/tree/canary/examples/with-msw
if (process.env.NODE_ENV === 'test') {
  // eslint-disable-next-line global-require
  const initMocks = require('../../mocks').default;
  initMocks();
}

export function reportWebVitals(metric: NextWebVitalsMetric) {
  const webVitalMetrics = new Metrics('WEB_VITALS');
  const pagename: Pages = getPageFromPathname(window.location.pathname);
  const metricName = `${metric.name}.${pagename}`;
  /**
   * Try catch is being implemented as a fail safe since it would not be caught
   * within the error boundary. Metrics would ideally not throw errors
   */
  try {
    webVitalMetrics.publishCounterMetric(metricName, metric.value);
    webVitalMetrics.publishString('href', window.location.href);
  } catch (error) {
    const logContext = { href: window.location.href };
    logger.error(Logs.webVitalsFailure, error as Error, logContext);
  }
}

export const App = ({ Component, pageProps }: AppProps) => (
  <div id="seller-forums" className="katal min-h-[85vh]">
    <Script src={assetUrls.urls.prod} />
    <Helmet htmlAttributes={{ dir: typeof window !== 'undefined' && document.documentElement.dir ? document.documentElement.dir : 'ltr' }}>
      <meta name="viewport" content="width=1280" />
      {
      typeof window !== 'undefined' && <link rel="canonical" href={window.location.href.split('?')[0]} />
    }
    </Helmet>
    <ErrorBoundary>
      <I18NWrapper>
        <>
          <RouteHistory />
          <UserInfo>
            <RouteProtector>
              <PermissionsModal />
              <AppNavbar />
              <SearchBar />
              <GlobalBanners />
              <Component {...pageProps} />
            </RouteProtector>
          </UserInfo>
        </>
      </I18NWrapper>
    </ErrorBoundary>
  </div>
);

const AppWrapper = (props: AppProps<ForumsPageProps>) => {
  const { pageProps, router } = props;
  const isSSRPage = getIsSSRPage(router.route, pageProps.weblabList);
  return (
    <ErrorBoundary>
      <HelmetProvider>
        <StoreProvider {...pageProps}>
          {isSSRPage
            ? SSRApp(props)
            : App(props)}
        </StoreProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
};

export default AppWrapper;
